import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import './Privacy.css';

const EFFECTIVE_DATE = 'September 26, 2026';

function Privacy() {
  return (
    <div className="privacy-page">
      <Helmet>
        <title>Privacy Policy | Mountain Pine Beetle Foundation</title>
        <meta name="description" content="How the Mountain Pine Beetle Foundation collects, uses, protects, and publishes information submitted through mountainpinebeetle.org." />
        <link rel="canonical" href="https://www.mountainpinebeetle.org/privacy" />
      </Helmet>

      <Container fluid className="privacy-header py-5">
        <Container>
          <h1 className="mb-2">Privacy Policy</h1>
          <p className="mb-0">Effective {EFFECTIVE_DATE}</p>
        </Container>
      </Container>

      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={9}>
            <p className="lead">
              This policy explains what information the Mountain Pine Beetle Foundation (&ldquo;the
              Foundation,&rdquo; &ldquo;we&rdquo;) collects through mountainpinebeetle.org, how we use
              and protect it, and what we publish.
            </p>

            <h2>Information we collect</h2>
            <h3>Contact form</h3>
            <p>
              Your name, email address, and message, plus an optional phone number and organization,
              the type of inquiry, and a subject line.
            </p>
            <h3>Property Evaluation (Right of Entry) form</h3>
            <p>
              The property&rsquo;s address, county, parcel number, type, acreage, and HOA details; the
              owner&rsquo;s name, email, phone, and mailing address; access instructions you choose to
              give us (for example gate codes, pets, or hazards); your preferred contact method and
              time; and your electronic signature and its date.
            </p>
            <h3>Tree observations</h3>
            <p>
              When our field crews or partners inspect trees, we record each tree&rsquo;s location,
              species, health status, measurements, and observation date, optional field notes, and,
              where applicable, the organization sponsoring the work.
            </p>
            <h3>Technical information</h3>
            <p>
              Our servers log IP addresses, requested pages, and browser details to keep the site
              secure and prevent abuse. Actions taken by Foundation staff and authorized field crews
              in our systems are also logged for accountability.
            </p>

            <h2>How we use it</h2>
            <ul>
              <li>To respond to your inquiries and requests.</li>
              <li>To schedule and carry out property evaluations, including giving advance notice before entry.</li>
              <li>To plan treatment and monitoring, and for research, education, and conservation.</li>
              <li>To secure the website and our systems.</li>
            </ul>
            <p>We do not sell or rent personal information, and we do not use it for advertising.</p>

            <h2>What we publish</h2>
            <p>
              Our <Link to="/impact">Impact Map</Link> and our data feed for research partners show
              tree observations so the public can see the scale of the outbreak and our response.
              For each tree they include the species, health status, observation date, basic
              measurements, confidence level, and the sponsoring organization, if any.
            </p>
            <ul>
              <li>
                <strong>Locations are approximate.</strong> Published locations are rounded to a grid
                of about 110&nbsp;meters (roughly 360&nbsp;feet), and trees in the same grid square
                are shown together. Exact locations are available only to Foundation staff and
                authorized field crews.
              </li>
              <li>
                <strong>We never publish</strong> names, addresses, contact details, parcel numbers,
                access instructions, field notes, or who recorded an observation.
              </li>
              <li>
                When an individual person sponsors work, their name is not published; only the
                sponsor type (&ldquo;Individual&rdquo;) is shown.
              </li>
            </ul>

            <h2>Service providers</h2>
            <p>
              We use a limited number of outside services to run the Foundation, and we make
              commercially reasonable efforts to choose providers with appropriate data-handling,
              security, and privacy safeguards:
            </p>
            <ul>
              <li><strong>Hosting.</strong> Our website and database run on servers operated by a hosting provider.</li>
              <li>
                <strong>AI services.</strong> As described on our forms, information you submit may be
                processed by third-party service providers, including large language model (LLM) and
                other AI services, for intake, categorization, summarization, translation, drafting,
                and communications on the Foundation&rsquo;s behalf.
              </li>
              <li>
                <strong>Map imagery.</strong> The Impact Map loads map tiles from Esri and
                OpenStreetMap. Those providers receive your IP address and the map area you are
                viewing, only while you use that page.
              </li>
            </ul>
            <p>We may also disclose information when required by law.</p>

            <h2>How we protect it</h2>
            <p>
              All traffic to the site is encrypted with HTTPS. Sensitive fields &mdash; email
              addresses, phone numbers, access instructions, electronic signatures, and field notes
              &mdash; are additionally encrypted in our database. Access is limited to authorized
              staff with individual accounts, and staff activity is logged. No system is perfectly
              secure, but we work to protect your information and review our safeguards regularly.
            </p>

            <h2>Cookies and browser storage</h2>
            <p>
              We use only what the site needs to work: a session cookie when staff sign in, a
              security cookie that protects forms against forgery, and browser storage that remembers
              you&rsquo;ve seen our cookie notice (and, for staff, the last map position in admin
              tools). We use no analytics, advertising, or tracking cookies, and our fonts are served
              from our own servers.
            </p>
            <p>
              Our field-crew tool, used only by authorized crews, stores their work on their own
              device until they sync it, and explains this within the tool.
            </p>

            <h2>How long we keep it</h2>
            <p>
              We keep personal information for as long as we need it for the purposes above &mdash;
              for example, to follow up on an inquiry, carry out an evaluation, or keep a record of
              treatment and monitoring over time &mdash; or until you ask us to delete it. Tree
              observations are kept long-term as part of our forest-health record; on request we
              remove the personal details connected to them. Security logs are rotated
              automatically, with older entries overwritten. Deleted information may remain in
              backups for a limited time.
            </p>

            <h2>Your choices</h2>
            <ul>
              <li>
                To ask what information we hold about you, or to correct or delete it, contact us
                through our <Link to="/contact">Contact page</Link> (choose &ldquo;Other&rdquo; and use
                the subject &ldquo;Privacy request&rdquo;).
              </li>
              <li>
                Property owners may revoke a Right of Entry at any time by written notice, as
                described in the agreement.
              </li>
            </ul>

            <h2>Children</h2>
            <p>
              This site is not directed at children under 13, and we do not knowingly collect their
              personal information.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              If we change this policy, we will update this page and the effective date above.
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Privacy;
